import {
  BriefSocialMediaAiFitCheckStatus,
  IBriefFitEvaluation,
  IBriefInfluencerV2
} from "@shared/lib/types/briefInfluencerV2";
import { Button, Popover } from "@shared/ui";
import { Flex } from "antd";
import { ArrowRight, CheckCircle2, MinusCircle } from "lucide-react";
import { useState } from "react";
import { AiFitReportModal } from "./AiFitReportModal";
import {
  AiMatchAnalyzeButton,
  AiMatchFailedAction,
  AiMatchRerunMenu
} from "./AiMatchActions";
import {
  AiFitAnalysis,
  checkFitCheckRetry,
  getFailureReason,
  getScoreColor,
  getScoreDisplay
} from "./lib/aiFitCheckUtils";
import { useAiMatchAnalyze } from "./lib/useAiMatchAnalyze";
import styles from "./styles.module.scss";

type AiAnalysis = AiFitAnalysis;

interface AiMatchCellProps {
  record: IBriefInfluencerV2;
  updateBriefInfluencer: (id: string, data: IBriefInfluencerV2) => void;
}

function ReasonList({
  title,
  items,
  variant,
}: {
  title: string;
  items?: string[];
  variant: "match" | "mismatch";
}) {
  if (!items?.length) return null;
  const isMatch = variant === "match";
  return (
    <div className={styles.aiReasonBlock}>
      <span
        className={`${styles.aiReasonTitle} ${
          isMatch ? styles.aiReasonTitleMatch : styles.aiReasonTitleMismatch
        }`}
      >
        {isMatch ? <CheckCircle2 size={11} /> : <MinusCircle size={11} />}
        {title}
      </span>
      <ul className={styles.aiReasonList}>
        {items.map((item, i) => (
          <li
            key={`${variant}-${i}`}
            className={`${styles.aiReasonItem} ${
              isMatch ? styles.aiReasonItemMatch : styles.aiReasonItemMismatch
            }`}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AiMatchSuccessPopover({
  analysis,
  score,
  color,
  onViewFullReport,
}: {
  analysis: AiAnalysis;
  score: string;
  color: string;
  onViewFullReport: () => void;
}) {
  const [open, setOpen] = useState(false);
  const fit = analysis.creator_campaign_fit;
  const reasons = analysis.fit_reasons;
  const overallScore = fit?.overall_score;
  const scoreDisplay = getScoreDisplay(overallScore);

  const content = (
    <div className={styles.aiPopover}>
      <div
        className={`${styles.aiPopoverHero} ${styles[scoreDisplay.heroClass]}`}
      >
        <span className={styles.aiPopoverVerdict}>
          {fit?.verdict || "AI Analysis"}
        </span>
        <div className={styles.aiPopoverScoreWrap}>
          <span
            className={styles.aiPopoverScoreValue}
            style={{ color: getScoreColor(overallScore) }}
          >
            {overallScore ?? "-"}
          </span>
          <span className={styles.aiPopoverScoreLabel}>Fit score</span>
        </div>
      </div>

      <div className={styles.aiPopoverBodyWrap}>
        <ReasonList
          title="Top match"
          items={reasons?.top_match_reasons}
          variant="match"
        />
        <ReasonList
          title="Top mismatch"
          items={reasons?.top_mismatch_reasons}
          variant="mismatch"
        />
      </div>

      <div className={styles.aiPopoverFooter}>
        <Button
          type="primary"
          block
          className={styles.aiReportButton}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
            onViewFullReport();
          }}
        >
          View full report
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="hover"
      placement="right"
      open={open}
      onOpenChange={setOpen}
      overlayClassName={styles.aiPopoverOverlay}
    >
      <span className={styles.aiMatchLabel} style={{ color }}>
        {score}
      </span>
    </Popover>
  );
}

export function AiMatchDisplay({
  fitCheck,
  isAnalyzing,
  onAnalyze,
}: {
  fitCheck?: IBriefFitEvaluation | null;
  isAnalyzing: boolean;
  onAnalyze: () => void;
}) {
  const [isReportOpen, setIsReportOpen] = useState(false);

  const analyzeAction = (
    <AiMatchAnalyzeButton isAnalyzing={isAnalyzing} onAnalyze={onAnalyze} />
  );

  if (!fitCheck) {
    return analyzeAction;
  }

  if (
    fitCheck.status === BriefSocialMediaAiFitCheckStatus.Evaluating ||
    fitCheck.status === BriefSocialMediaAiFitCheckStatus.WaitingCreatorProfile ||
    fitCheck.status === BriefSocialMediaAiFitCheckStatus.WaitingPrerequisites
  ) {
    return (
      <Flex align="center" gap={6} className={styles.aiMatchPending}>
        <span>Analyzing...</span>
      </Flex>
    );
  }

  if (fitCheck.status === BriefSocialMediaAiFitCheckStatus.Failed) {
    return (
      <AiMatchFailedAction
        failedReason={getFailureReason(fitCheck)}
        isAnalyzing={isAnalyzing}
        onRerun={onAnalyze}
        isRetryable={checkFitCheckRetry(fitCheck)}
      />
    );
  }

  const analysis = fitCheck.aiAnalysis;
  if (!analysis) {
    return analyzeAction;
  }

  const fit = analysis.creator_campaign_fit;
  const overallScore = fit?.overall_score;
  const { color } = getScoreDisplay(overallScore);
  const score =
    overallScore !== undefined && overallScore !== null
      ? String(overallScore)
      : "-";

  return (
    <>
      <Flex align="center" gap={20} className={styles.aiMatchCell}>
        <AiMatchSuccessPopover
          analysis={analysis}
          score={score}
          color={color}
          onViewFullReport={() => setIsReportOpen(true)}
        />
        <AiMatchRerunMenu isAnalyzing={isAnalyzing} onRerun={onAnalyze} />
      </Flex>
      {isReportOpen ? (
        <AiFitReportModal
          open={isReportOpen}
          onClose={() => setIsReportOpen(false)}
          analysis={analysis}
        />
      ) : null}
    </>
  );
}

export function AiMatchCell({
  record,
  updateBriefInfluencer,
}: AiMatchCellProps) {
  const { handleAnalyze, isAnalyzing } = useAiMatchAnalyze(
    record._id,
    updateBriefInfluencer
  );
  const fitCheck = record.briefFitEvaluationJob;

  return (
    <AiMatchDisplay
      fitCheck={fitCheck}
      isAnalyzing={isAnalyzing}
      onAnalyze={handleAnalyze}
    />
  );
}
